import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, TouchableOpacity } from 'react-native';

import Clipboard from '@react-native-clipboard/clipboard';
import { useNavigation } from '@react-navigation/native';

import ChevronRight from '@/assets/icons/chevronRightCasino';
import CopyIcon from '@/assets/icons/copy-small';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Column, Row } from '@/components/lib/components';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { AccountTabNavigationProp } from '@/navigation/types';
import { useIsMutating } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import { CasinoTransaction } from '../hooks/use-casino-history';
import { CASINO_ROUND_DETAILS_MUTATION_KEY, useCasinoRoundDetails } from '../hooks/use-casino-round-details';

type CasinoHistoryCardProps = {
    transaction: CasinoTransaction;
};

type BaseCardProps = {
    gameName: string;
    date: string;
    externalId: string;
    roundId: string;
    displayAmount: string;
    cardType?: 'Entry' | 'Paid';
};

const BaseCard = ({ gameName, date, externalId, roundId, displayAmount, cardType = 'Entry' }: BaseCardProps) => {
    const { t } = useTranslation('casino');
    const { showToast } = useAlerts();
    const { navigate } = useNavigation<AccountTabNavigationProp>();
    const { mutateAsync: fetchRoundDetails, isPending } = useCasinoRoundDetails();
    const isAnyRoundDetailsLoading = useIsMutating({ mutationKey: CASINO_ROUND_DETAILS_MUTATION_KEY }) > 0;
    const enableRoundDetails = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings?.enable_round_details
    );

    const cardTitle = cardType === 'Entry' ? t('entry_placed', { gameName }) : t('entry_result', { gameName });

    const parsedDate = parseISO(date);
    const formattedDate = format(parsedDate, 'M/d/yyyy');
    const formattedTime = format(parsedDate, 'h:mm a');

    const copyExternalId = () => {
        Clipboard.setString(externalId);
        showToast({ message: t('copied_id'), toastType: 'success' });
    };

    const onCardPress = async () => {
        if (isPending || !roundId) return;
        try {
            const result = await fetchRoundDetails(roundId);
            const url = result?.data;
            if (url) {
                navigate('ModalWebView', { uri: url });
            } else {
                showToast({ message: t('no_round_details_available'), toastType: 'warning' });
            }
        } catch (e) {
            showToast({ message: t('no_round_details_available'), toastType: 'warning' });
        }
    };

    return (
        <TouchableOpacity
            onPress={enableRoundDetails ? onCardPress : undefined}
            disabled={!enableRoundDetails || isAnyRoundDetailsLoading}
            testID="casinoHistoryCard"
            accessible={false}
        >
            <Row py="s16" justifyContent="space-between" gap="s8">
                <Column flex={1}>
                    <Text color={'neutral300'} variant="labelMedium" textTransform="uppercase" marginBottom="s8">
                        {t('date_at_time', { date: formattedDate, time: formattedTime })}
                    </Text>
                    <Text variant="titleMedium" numberOfLines={1} marginBottom="s2" testID="casinoHistoryCardTitle">
                        {cardTitle}
                    </Text>
                    <Pressable onPress={copyExternalId} hitSlop={8} testID="casinoHistoryCopyId">
                        <Row alignItems="center" gap="s8">
                            <Text color={'neutral300'} variant="bodySmall">
                                {t('external_id', { id: externalId })}
                            </Text>
                            <CopyIcon />
                        </Row>
                    </Pressable>
                </Column>
                <Row alignItems="center" gap="s8">
                    <Text variant="bodyMedium" testID="casinoHistoryCardAmount">
                        {displayAmount}
                    </Text>
                    {enableRoundDetails ? isPending ? <ActivityIndicator size="small" /> : <ChevronRight /> : null}
                </Row>
            </Row>
            <LineSeparator />
        </TouchableOpacity>
    );
};

export const CasinoHistoryCard = ({ transaction }: CasinoHistoryCardProps) => {
    const { game_name, date, status, winning_amount, external_id, round_id, amount } = transaction;

    const entryAmount = `-${Math.abs(amount).toFixed(2)}`;
    const isWon = status.toUpperCase() === 'WON';
    const resultAmount = `+${(isWon ? winning_amount : 0).toFixed(2)}`;

    return (
        <>
            {/**Result Card */}
            <BaseCard
                gameName={game_name}
                date={date}
                externalId={external_id}
                roundId={round_id}
                displayAmount={resultAmount}
                cardType="Paid"
            />
            {/**Entry Card */}
            <BaseCard
                gameName={game_name}
                date={date}
                externalId={external_id}
                roundId={round_id}
                displayAmount={entryAmount}
            />
        </>
    );
};
